import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  Paper,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import QuizCard from '../../../components/quiz/QuizCard';
import LiveQuizCard from '../../../components/quiz/LiveQuizCard';
import { useStudentQuizzes } from '../../../hooks/useStudentQuizzes';
import { QuizAttempt, QuizLiveInfo, QuizSummary } from '../../../types/quiz';
import { isLiveQuizEnded, isLiveQuizRunning } from '../../../utils/liveQuizStatus';

type QuizzesTabKey = 'live' | 'self';

const RECENTLY_ENDED_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Live defaults to the front page whenever there's something worth seeing
 * there: upcoming, actually running, or ended recently enough that a
 * student might still be looking for the result. Otherwise Self-paced is
 * the more useful landing tab.
 */
function shouldDefaultToLiveTab(quizzes: QuizSummary[], nowMs: number): boolean {
  return quizzes.some(quiz => {
    if (!quiz.live) {
      return false;
    }
    if (isLiveQuizRunning(quiz.live) || !isLiveQuizEnded(quiz.live)) {
      return true;
    }
    // Ended — still worth defaulting to Live if it wrapped up recently.
    const startsAtMs = Date.parse(quiz.live.startsAt);
    return nowMs - startsAtMs <= RECENTLY_ENDED_WINDOW_MS;
  });
}

function matchesSearch(quiz: QuizSummary, query: string): boolean {
  if (!query) {
    return true;
  }
  const needle = query.toLowerCase();
  return (
    quiz.title.toLowerCase().includes(needle) ||
    (!!quiz.description && quiz.description.toLowerCase().includes(needle))
  );
}

/** A quiz known (by type, not just convention) to carry live info. */
interface QuizWithLiveInfo extends QuizSummary {
  live: QuizLiveInfo;
}

function hasLiveInfo(quiz: QuizSummary): quiz is QuizWithLiveInfo {
  return quiz.live != null;
}

/**
 * Live events sorted `startsAt` ascending, except a running quiz is always
 * pinned above everything upcoming — a scoreboard in progress outranks a
 * countdown regardless of which technically starts sooner elsewhere. Ended
 * quizzes sink to their own group at the end, behind a divider.
 */
function sortLiveQuizzes(quizzes: QuizSummary[]): { current: QuizSummary[]; past: QuizSummary[] } {
  const withLive = quizzes.filter(hasLiveInfo);
  const byStartsAtAsc = (a: QuizWithLiveInfo, b: QuizWithLiveInfo) =>
    Date.parse(a.live.startsAt) - Date.parse(b.live.startsAt);

  const running = withLive.filter(q => isLiveQuizRunning(q.live)).sort(byStartsAtAsc);
  const upcoming = withLive
    .filter(q => !isLiveQuizRunning(q.live) && !isLiveQuizEnded(q.live))
    .sort(byStartsAtAsc);
  const ended = withLive.filter(q => isLiveQuizEnded(q.live)).sort(byStartsAtAsc);

  return { current: [...running, ...upcoming], past: ended };
}

function QuizzesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, error } = useStudentQuizzes();
  const quizzes = useMemo(() => data?.quizzes ?? [], [data]);

  const serverNowMs = data ? Date.parse(data.serverNow) : Date.now();
  const defaultTab: QuizzesTabKey = shouldDefaultToLiveTab(quizzes, serverNowMs) ? 'live' : 'self';
  const activeTab = (searchParams.get('tab') as QuizzesTabKey | null) ?? defaultTab;

  const handleTabChange = (_event: React.SyntheticEvent, value: QuizzesTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next);
  };

  const liveQuizzes = useMemo(
    () => quizzes.filter(quiz => quiz.quizMode === 'live' && matchesSearch(quiz, searchQuery)),
    [quizzes, searchQuery]
  );
  const selfPacedQuizzes = useMemo(
    () => quizzes.filter(quiz => quiz.quizMode !== 'live' && matchesSearch(quiz, searchQuery)),
    [quizzes, searchQuery]
  );

  const { current: currentLiveQuizzes, past: pastLiveQuizzes } = useMemo(
    () => sortLiveQuizzes(liveQuizzes),
    [liveQuizzes]
  );

  const handleStartQuiz = (quizId: number) => {
    navigate(`/student/quizzes/${quizId}/play`);
  };

  const handleViewResults = (quizId: number) => {
    navigate(`/student/quizzes/${quizId}/results`);
  };

  const activeCount = activeTab === 'live' ? liveQuizzes.length : selfPacedQuizzes.length;

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Quizzes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Live events run on a schedule with everyone at once; self-paced quizzes are ready
            whenever you are.
          </Typography>
        </Box>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Live events" value="live" />
          <Tab label="Self-paced" value="self" />
        </Tabs>

        {/* Search Bar */}
        <Paper sx={{ mb: 4, p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search quizzes by title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* The demo tour spotlights this region. The anchor sits on the wrapper rather than on the
            results grid inside it because the grid only exists once the data has loaded and is
            non-empty — while loading this is a spinner, and with no quizzes it is an empty state.
            Anchored to the grid, the tour would wait for a target that never appears and silently
            skip the step. */}
        <Box data-demo-id="quizzes-list">
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load quizzes. Please try again.
          </Alert>
        ) : activeCount === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {searchQuery
                ? 'No quizzes found matching your search.'
                : activeTab === 'live'
                  ? 'No live events scheduled right now.'
                  : 'No quizzes available yet.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {searchQuery ? 'Try adjusting your search terms.' : 'Check back later for new quizzes!'}
            </Typography>
          </Paper>
        ) : activeTab === 'live' ? (
          <>
            <Grid container spacing={3} data-demo-id="quizzes-list">
              {currentLiveQuizzes.map(quiz => (
                <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                  <LiveQuizCard quiz={quiz} />
                </Grid>
              ))}
            </Grid>

            {pastLiveQuizzes.length > 0 && (
              <>
                <Divider sx={{ my: 4 }}>
                  <Typography variant="overline" color="text.secondary">
                    Past events
                  </Typography>
                </Divider>
                <Grid container spacing={3}>
                  {pastLiveQuizzes.map(quiz => (
                    <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                      <LiveQuizCard quiz={quiz} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        ) : (
          <Grid container spacing={3} data-demo-id="quizzes-list">
            {selfPacedQuizzes.map((quiz) => (
              <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                <QuizCard
                  quiz={quiz}
                  // QuizCard (untouched by design) wants a full QuizAttempt;
                  // the list response's `myAttempt` is intentionally the
                  // Pick<id|score|completedAt> subset — those are the only
                  // fields QuizCard actually reads (`completedAt`, `score`).
                  attempt={quiz.myAttempt as QuizAttempt | null | undefined}
                  onStart={() => handleStartQuiz(quiz.id)}
                  onViewResults={() => handleViewResults(quiz.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
        </Box>
      </Container>
    </MainLayout>
  );
}

export default QuizzesPage;
